<script setup>
// NOTICE: KAPTION-INTEGRATION
// reply-template-picker.PICKER.1..3 — thin modal around the new-conversation
// wizard's TemplateGalleryStep / CreateTemplateStep (both reused unchanged).
// The conversation is already the recipient, so there is no stepper; the
// subtitle names the contact and the inbox. Templates come from THIS
// conversation's inbox only.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import TemplateGalleryStep from 'dashboard/components-next/NewConversationModal/steps/TemplateGalleryStep.vue';
import CreateTemplateStep from 'dashboard/components-next/NewConversationModal/steps/CreateTemplateStep.vue';
import { listPickerTemplates } from './kaptionReplyTemplateHelpers';

const props = defineProps({
  inbox: { type: Object, required: true },
  contactName: { type: String, default: '' },
  selectedTemplate: { type: Object, default: null },
  // template-gallery-rumo.LANG.1 — Rumo templates open in this language.
  conversationLanguage: { type: String, default: '' },
});

const emit = defineEmits(['close', 'select']);

const { t } = useI18n();
const store = useStore();
const { canUseFeature } = useFeaturePermissions();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const getInbox = useMapGetter('inboxes/getInbox');

const isCreating = ref(false);

// reply-template-picker.TYPES.6 — unsendable shapes stay listed (disabled).
const templates = computed(() =>
  listPickerTemplates(getInbox.value(props.inbox.id))
);

// Same gate as the wizard's "+ Crear plantilla".
const canCreateTemplate = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT') &&
    canUseFeature('whatsapp_template_management')
);

const subtitle = computed(() =>
  t('KAPTION_REPLY_TEMPLATE.MODAL.SUBTITLE', {
    contact: props.contactName || t('KAPTION_REPLY_TEMPLATE.MODAL.CONTACT'),
    inbox: props.inbox.name || '',
  })
);

const close = () => emit('close');
const select = template => emit('select', template);
const onCreated = template => {
  isCreating.value = false;
  select(template);
};

onMounted(() => {
  // Refresh the inbox list so templates approved or created elsewhere show up.
  store.dispatch('inboxes/get').catch(() => {});
});

useKeyboardEvents({
  Escape: {
    action: () => {
      if (isCreating.value) {
        isCreating.value = false;
        return;
      }
      close();
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2 backdrop-blur-[6px]"
      data-testid="reply-template-modal"
      @click.self="close"
    >
      <div
        class="max-w-full max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden bg-n-alpha-3 border border-n-strong shadow-2xl backdrop-blur-[100px] rounded-2xl"
        :class="isCreating ? 'w-[64rem]' : 'w-[46rem]'"
      >
        <div
          class="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b shrink-0 border-n-weak"
        >
          <div class="flex items-center min-w-0 gap-3">
            <span
              class="flex items-center justify-center rounded-xl size-9 shrink-0 bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-file-text size-[1.15rem]" />
            </span>
            <div class="min-w-0">
              <h2 class="text-base font-semibold leading-tight text-n-slate-12">
                {{ t('KAPTION_REPLY_TEMPLATE.MODAL.TITLE') }}
              </h2>
              <p
                class="text-xs leading-tight truncate text-n-slate-10"
                data-testid="reply-template-modal-subtitle"
              >
                {{ subtitle }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="flex items-center justify-center transition-colors rounded-lg size-10 shrink-0 text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12"
            data-testid="reply-template-modal-close"
            :aria-label="t('KAPTION_REPLY_TEMPLATE.MODAL.CLOSE')"
            @click="close"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="px-6 py-5 overflow-y-auto">
          <CreateTemplateStep
            v-if="isCreating"
            :inbox="inbox"
            @created="onCreated"
            @back="isCreating = false"
          />
          <TemplateGalleryStep
            v-else
            :templates="templates"
            :inbox-id="inbox.id"
            :conversation-language="conversationLanguage"
            :selected-template="selectedTemplate"
            :can-create="canCreateTemplate"
            @select="select"
            @create="isCreating = true"
            @back="close"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>
